import React, { useRef, useCallback, useEffect, useState } from 'react'
import * as Yup from 'yup'
import {
  Formik,
  Form,
  Field,
  FormikHelpers,
  FieldProps,
  FormikProps,
} from 'formik'
import { toast } from 'react-toastify'
import {
  ValidationMessages,
  FormOptions,
  NUMBER,
  PAIN_SCALE_MAPPING,
} from './Constants'
import { toastOptions } from '../../features/lms/models'
import {
  addBaiscHealthInfo,
  getAllCampAppointments,
} from '../../services/CampManagementService'

interface Complaint {
  complaint: string
  duration: string
  notes: string
}

interface VitalsFormValues {
  vitals: {
    campId: string
    patientUhid: string
    encounterId: string
    visitType: string
    pulse: string | number
    temperature: string | number
    respiratoryRate: string | number
    spo2: string | number
    systolic: string | number
    diastolic: string | number
    posture: string
    glucoseType: string
    rbs: string | number
    fbs: string | number
    ppbs: string | number
    height: string | number
    weight: string | number
    bmi: string | number
    document: File | null
    documentUrl?: string
    sugarReportUrl?: string
  }
  history: {
    wbps: string
    chiefComplaints: string
    historyOfPresentIllness: string
    pastHistory: string
    pastMedicalHistory: string
    diet: string
    sleep: string
    bowelBladder: string
    smokingHistory: string
    alcoholicHistory: string
    habitualHistory: string
    obstetricHistory: string
    familyHistory: string
    historyDrugAllergies: string
    previousSurgeries: string
    otherConditions: string
    height: string | number
    heightUnit: string
    weight: string | number
    weightUnit: string
    pallor: string
    icterus: string
    cyanosis: string
    clubbing: string
    lymphadenopathy: string
    edema: string
    additionalNotes: string
  }
  chiefComplaints: Complaint[]
  systemic: {
    localExamination: string
    cardiovascularSystem: string
    respiratorySystem: string
    nervousSystem: string
    perAbdomen: string
  }
}

// Validation is handled in the validationSchema object below

import {
  Box,
  Card,
  CardContent,
  InputAdornment,
  CardHeader,
  IconButton,
  Collapse,
  Button,
  styled,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Grid,
  Divider,
  Select,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'

import styles from './vitals.module.scss'
import {
  COMPLAINTS,
  MEDICAL_HISTORY_DEFAULTS,
  textHeightRequire,
  textHours,
  textMissing,
  textNoVisit,
  textWeightRequire,
  TYPES_OF_GLUCOSE,
} from '../campManagement/constants/Constants'
import Height from './Height'

// Styled components
const StyledCard = styled(Card)({
  marginBottom: '16px',
  '&:first-of-type': {
    marginTop: 0,
  },
  '&:last-child': {
    marginBottom: 0,
  },
  '& .MuiTypography-root': {
    fontFamily: 'Noto Sans, sans-serif',
    color: '#363463',
  },
  '& .MuiCardHeader-root': {
    padding: '8px 16px',
    cursor: 'pointer',
  },
  '& .MuiFormControlLabel-root': {
    margin: 0,
    paddingTop: 0,
    paddingBottom: 0,
    '& .MuiFormControlLabel-label': {
      marginLeft: '4px',
      fontSize: '0.8rem',
    },
  },
  '& .medical-conditions-grid': {
    marginTop: '8px',
    marginBottom: '16px',
  },
  '& .ChiefComplaints-container': {
    padding: '0 16px 8px',
    '& .ChiefComplaints-title': {
      fontSize: '14px',
      fontWeight: 600,
      margin: '4px 0 8px',
    },
    '& .ChiefComplaints-gridContainer': {
      maxHeight: '300px',
      overflowY: 'auto',
    },
    '& .ChiefComplaints-complaintItemVitals': {
      minHeight: '32px',
      padding: '0 6px',
      margin: '2px 0',
      '& .MuiFormControlLabel-root': {
        margin: 0,
        '& .MuiFormControlLabel-label': {
          fontSize: '13px',
        },
      },
    },
  },
})

const SectionTitle = styled(Typography)({
  color: '#363463',
  marginBottom: '16px',
})

const ExpandButton = styled(IconButton)({
  marginLeft: 'auto',
  padding: '4px',
  color: '#363463',
  '&:hover, &:focus': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
  '&.Mui-focusVisible': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    color: '#9e9e9e',
  },
  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  '&:active': {
    boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
  },
})

const ButtonContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  right: 0,
  left: 0,
  backgroundColor: '#fff',
  padding: theme.spacing(2, 3),
  display: 'flex',
  justifyContent: 'center',
  boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
  zIndex: theme.zIndex.appBar,
}))

const StyledDivider = styled(Divider)({
  marginBottom: '16px',
})

const SectionSubtitle = styled(Typography)({
  marginTop: '16px',
  marginBottom: '8px',
})

const StyledCardHeader = styled(CardHeader)({
  backgroundColor: '#f5f5f5',
  padding: '8px 16px',
  cursor: 'pointer',
  '& .MuiCardHeader-title': {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#363463',
  },
})

const StyledCardContent = styled(CardContent)({
  padding: '16px',
  '& .MuiInputBase-root': {
    fontSize: '14px',
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#fff',
  },
})

const SubmitButton = styled(Button)({
  width: 'auto',
  minWidth: '150px',
  padding: '6px 16px',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  backgroundColor: '#1976d2',
  '&:hover': {
    backgroundColor: '#1565c0',
  },
  '&:disabled': {
    backgroundColor: '#e0e0e0',
    color: '#9e9e9e',
  },
  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  '&:active': {
    boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
  },
})

const StyledTextField = styled(TextField)({
  marginBottom: '16px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px',
    backgroundColor: '#ffffff',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#4682b4',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#4682b4',
      borderWidth: '1px',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '14px',
    '&.Mui-focused': {
      color: '#4682b4',
    },
  },
  '&:disabled': {
    backgroundColor: '#cccccc',
    color: '#666666',
  },
})

interface VitalsProps {
  campId: string
  patientUhid: string
  isView?: boolean
  getPatientList?: () => void
}

const Vitals: React.FC<VitalsProps> = ({
  campId,
  patientUhid,
  isView = false,
  getPatientList,
}): JSX.Element => {
  const formikRef = useRef<FormikProps<VitalsFormValues> | null>(null)
  // Chief Complaints State
  const [complaints, setComplaints] = useState<
    Array<{
      id: string
      name: string
      selected: boolean
      duration?: number
      durationUnit?: 'hours'
    }>
  >([])
  const [comment, setComment] = useState('')

  // Past Medical History State
  const [medicalHistory, setMedicalHistory] = useState<
    Array<{ id: string; name: string; selected: boolean }>
  >(MEDICAL_HISTORY_DEFAULTS)
  const [previousSurgeries, setPreviousSurgeries] = useState('')
  const [drugAllergies, setDrugAllergies] = useState('')
  const [otherConditions, setOtherConditions] = useState('')
  const [additionalNotes, setAdditionalNotes] = useState('')

  // Handler for medical history checkboxes
  const handleMedicalHistoryChange = (id: string) => {
    setMedicalHistory((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, selected: !item.selected } : item
      )
    )
  }

  const [isLoadingEncounter, setIsLoadingEncounter] = useState(false)

  // Function to fetch and set the encounter ID
  const fetchAndSetEncounterId = async () => {
    if (!campId || !patientUhid || isLoadingEncounter) return null

    try {
      setIsLoadingEncounter(true)
      const appointmentsResponse = await getAllCampAppointments(
        campId,
        patientUhid
      )

      if (
        appointmentsResponse?.success &&
        appointmentsResponse?.data?.length > 0
      ) {
        const visitsArray = appointmentsResponse?.data?.map((visit: any) => {
          return {
            visitId: visit?.visitId,
            speciality: visit?.speciality,
          }
        })

        return visitsArray
      }
      return []
    } catch (error) {
      console.error('Error fetching encounter ID:', error)
      return []
    } finally {
      setIsLoadingEncounter(false)
    }
  }

  // Constants for Chief Complaints
  const DURATION_OPTIONS = [
    { value: 24, label: '24 hours' },
    { value: 48, label: '48 hours' },
    { value: 72, label: '72 hours' },
  ]

  // Glucose type options
  const GLUCOSE_TYPES = [
    { value: 'rbs', label: 'Random' },
    { value: 'fbs', label: 'Fasting' },
    { value: 'ppbs', label: 'Post Prandial' },
  ]

  // Initialize complaints callback
  const initializeComplaints = useCallback(() => {
    if (COMPLAINTS.length > 0 && complaints.length === 0) {
      setComplaints(
        COMPLAINTS.map((name, index) => ({
          id: `complaint-${index}`,
          name,
          selected: false,
          duration: 1,
          durationUnit: 'hours' as const,
        }))
      )
    }
  }, [complaints.length])

  // Initialize complaints
  useEffect(() => {
    initializeComplaints()
  }, [initializeComplaints])

  // Update form values when complaints change
  useEffect(() => {
    if (formikRef.current) {
      const selectedComplaints = complaints
        .filter((complaint) => complaint.selected)
        .map((complaint) => ({
          complaint: complaint.name,
          duration: `${complaint.duration || 1} ${complaint.durationUnit}`,
          notes: '',
        }))

      formikRef.current.setFieldValue('chiefComplaints', selectedComplaints)
    }
  }, [complaints])

  // Chief Complaints Handlers
  const handleComplaintChange =
    (id: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setComplaints((prev) =>
        prev.map((complaint) =>
          complaint.id === id
            ? {
                ...complaint,
                selected: event.target.checked,
                duration: event.target.checked ? 1 : undefined,
                durationUnit: 'hours',
              }
            : complaint
        )
      )
    }

  const handleDurationChange = (id: string, value: number) => {
    setComplaints((prev) =>
      prev.map((complaint) =>
        complaint.id === id ? { ...complaint, duration: value } : complaint
      )
    )
  }

  const handleCustomDurationChange = (id: string, value: string) => {
    const numValue = parseInt(value)
    if (!isNaN(numValue)) {
      setComplaints((prev) =>
        prev.map((complaint) =>
          complaint.id === id
            ? { ...complaint, duration: numValue, durationUnit: 'hours' }
            : complaint
        )
      )
    }
  }

  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setComment(event.target.value)
  }

  const [expanded, setExpanded] = useState<{
    vitals: boolean
    history: boolean
    systemicExamination: boolean
    chiefComplaints: boolean
    pastMedicalHistory: boolean
  }>({
    vitals: true,
    history: false,
    systemicExamination: false,
    chiefComplaints: false,
    pastMedicalHistory: false,
  })

  // Calculate BMI from weight (kg) and height (cm)
  const calculateBMI = (weight: number, height: number): string => {
    // Convert height from cm to meters
    const heightInMeters = height / 100
    const bmi = weight / (heightInMeters * heightInMeters)
    return bmi.toFixed(1)
  }

  // Update BMI when height or weight changes
  const updateBMI = (
    weight: string | number,
    height: string | number,
    formik: FormikProps<VitalsFormValues>
  ) => {
    const weightNum = Number(weight)
    const heightNum = Number(height)
    if (weightNum && heightNum) {
      const bmi = calculateBMI(weightNum, heightNum)
      formik.setFieldValue('vitals.bmi', bmi)
    } else {
      formik.setFieldValue('vitals.bmi', '')
    }
  }

  const toggleSection = (
    section:
      | 'vitals'
      | 'history'
      | 'systemicExamination'
      | 'chiefComplaints'
      | 'pastMedicalHistory'
  ): void => {
    setExpanded((prev: any) => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  const handleSubmit = async (
    values: VitalsFormValues,
    { setSubmitting }: FormikHelpers<VitalsFormValues>
  ) => {
    try {
      if (!campId || !patientUhid) {
        throw new Error(textMissing)
      }

      const visits = await fetchAndSetEncounterId()
      if (visits?.length === 0) {
        throw new Error(textNoVisit)
      }
      const payload = {
        campId: campId,
        patientUhid: patientUhid,
        visitInfo: visits ?? [],
        basicHealthInfo: {
          vitals: {
            campId: values?.vitals?.campId || campId || '',
            patientUhid: values?.vitals?.patientUhid || patientUhid || '',
            visitType: values?.vitals?.visitType || '',
            pulse: NUMBER?.parse(values?.vitals?.pulse) || 0,
            temperature: NUMBER?.parse(values?.vitals?.temperature) || 0,
            respiratoryRate:
              NUMBER?.parse(values?.vitals?.respiratoryRate) || 0,
            spo2: NUMBER?.parse(values?.vitals?.spo2) || 0,
            systolic: NUMBER?.parse(values?.vitals?.systolic) || 0,
            diastolic: NUMBER?.parse(values?.vitals?.diastolic) || 0,
            posture: values?.vitals?.posture || 'Sitting',
            rbs: (values?.vitals?.rbs || '').toString(),
            fbs: (values?.vitals?.fbs || '').toString(),
            ppbs: (values?.vitals?.ppbs || '').toString(),
            sugarReportUrl: '',
            height: NUMBER?.parse(values?.history?.height) || 0,
            weight: NUMBER?.parse(values?.history?.weight) || 0,
            bmi: (values?.vitals?.bmi || '').toString(),
          },
          historyAndExamination: {
            wbps: values?.history?.wbps || '',
            chiefComplaints: values?.history?.chiefComplaints || '',
            historyOfPresentIllness:
              values?.history?.historyOfPresentIllness || '',
            pastHistory: values?.history?.pastHistory || '',
            pastMedicalHistory: values?.history?.pastMedicalHistory || '',
            diet: values?.history?.diet || '',
            sleep: values?.history?.sleep || '',
            bowelBladder: values?.history?.bowelBladder || '',
            smokingHistory: values?.history?.smokingHistory || '',
            alcoholicHistory: values?.history?.alcoholicHistory || '',
            habitualHistory: values?.history?.habitualHistory || '',
            obstetricHistory: values?.history?.obstetricHistory || '',
            familyHistory: values?.history?.familyHistory || '',
            historyDrugAllergies: values?.history?.historyDrugAllergies || '',
            previousSurgeries: values?.history?.previousSurgeries || '',
            height: NUMBER?.parse(values?.history?.height) || 0,
            heightUnit: values?.history?.heightUnit || 'cm',
            weight: NUMBER?.parse(values?.history?.weight) || 0,
            weightUnit: values?.history?.weightUnit || 'kg',
            pallor: values?.history?.pallor || '',
            icterus: values?.history?.icterus || '',
            cyanosis: values?.history?.cyanosis || '',
            clubbing: values?.history?.clubbing || '',
            lymphadenopathy: values?.history?.lymphadenopathy || '',
            edema: values?.history?.edema || '',
          },
          systemicExamination: {
            localExamination: values?.systemic?.localExamination || '',
            cardiovascularSystem: values?.systemic?.cardiovascularSystem || '',
            respiratorySystem: values?.systemic?.respiratorySystem || '',
            nervousSystem: values?.systemic?.nervousSystem || '',
            perAbdomen: values?.systemic?.perAbdomen || '',
          },
          chiefComplaints: {
            patientUhid: values?.vitals?.patientUhid || patientUhid || '',
            campId: values?.vitals?.campId || campId || '',
            complaints: (values?.chiefComplaints || [])?.map((complaint) => ({
              name: complaint?.complaint || '',
              duration: complaint?.duration || '24Hours',
              otherDetails: complaint?.notes || '',
            })),
            additionalComments:
              comment || values?.history?.additionalNotes || '',
          },
          medicalHistory: {
            patientUhid: values?.vitals?.patientUhid || patientUhid || '',
            campId: values?.vitals?.campId || campId || '',
            pastMedicalHistories: medicalHistory
              ?.filter((item) => item?.selected)
              ?.map((item) => item?.name),
            previousSurgeries:
              previousSurgeries || values?.history?.previousSurgeries || 'None',
            drugAllergies:
              drugAllergies || values?.history?.historyDrugAllergies || 'None',
            otherConditions:
              otherConditions || values?.history?.otherConditions || 'None',
            additionalNotes:
              additionalNotes || values?.history?.additionalNotes || 'None',
            familyCancerHistories: [
              {
                relation: 'grandMother',
                cancerType: 'breast',
                diagnosisYear: '2010',
                remarks: 'death',
              },
              {
                relation: 'father',
                cancerType: 'lung',
                diagnosisYear: '2015',
                remarks: 'cured',
              },
            ],
          },
        },
      }
      // Call the API to save the data
      const response = await addBaiscHealthInfo(payload)

      if (response?.success) {
        toast.success(response?.message, toastOptions)
        getPatientList?.()
      } else {
        throw new Error(response?.message || 'Failed to save vitals')
      }
    } catch (error: any) {
      toast.error(
        error?.message || 'An error occurred while saving vitals',
        toastOptions
      )
    } finally {
      setSubmitting(false)
    }
  }

  const yesNoOptions = FormOptions?.YES_NO
  const dietOptions = FormOptions?.DIET
  const sleepOptions = FormOptions?.SLEEP
  const postureOptions = FormOptions?.POSTURE
  const bowelBladderOptions = FormOptions?.BLADDER

  // Define initial form values with proper typing
  const initialValues: VitalsFormValues = {
    vitals: {
      campId: campId || '',
      patientUhid: patientUhid || '',
      encounterId: '',
      visitType: 'opd/general',
      pulse: '',
      temperature: '',
      respiratoryRate: '',
      spo2: '',
      systolic: '',
      diastolic: '',
      posture: 'Sitting',
      glucoseType: TYPES_OF_GLUCOSE.RANDOM,
      rbs: '',
      fbs: '',
      ppbs: '',
      height: '',
      weight: '',
      bmi: '',
      document: null,
      documentUrl: '',
    },
    history: {
      wbps: '',
      chiefComplaints: '',
      historyOfPresentIllness: '',
      pastHistory: '',
      pastMedicalHistory: '',
      diet: '',
      sleep: '',
      bowelBladder: '',
      smokingHistory: '',
      alcoholicHistory: '',
      habitualHistory: '',
      obstetricHistory: '',
      familyHistory: '',
      historyDrugAllergies: '',
      previousSurgeries: '',
      height: '',
      heightUnit: 'cm',
      weight: '',
      weightUnit: 'kg',
      pallor: '',
      icterus: '',
      cyanosis: '',
      clubbing: '',
      lymphadenopathy: '',
      edema: '',
      otherConditions: '',
      additionalNotes: '',
    },
    chiefComplaints: [],
    systemic: {
      localExamination: '',
      cardiovascularSystem: '',
      respiratorySystem: '',
      nervousSystem: '',
      perAbdomen: '',
    },
  }

  const validationSchema = Yup?.object()?.shape({
    vitals: Yup?.object()?.shape({
      pulse: Yup?.number()
        ?.typeError(ValidationMessages?.PULSE?.NUMBER)
        ?.positive(ValidationMessages?.PULSE?.MIN)
        ?.integer(ValidationMessages?.PULSE?.WHOLE_NUMBER)
        ?.required(ValidationMessages?.PULSE?.REQUIRED),
      temperature: Yup?.number()
        ?.typeError(ValidationMessages?.TEMPERATURE?.REQUIRED)
        ?.required(ValidationMessages?.TEMPERATURE?.REQUIRED),
      respiratoryRate: Yup?.number()
        ?.typeError(ValidationMessages?.RESPIRATORY_RATE?.REQUIRED)
        ?.required(ValidationMessages?.RESPIRATORY_RATE?.REQUIRED)
        ?.integer(ValidationMessages?.RESPIRATORY_RATE?.WHOLE_NUMBER),
      spo2: Yup?.number()
        ?.typeError(ValidationMessages?.SPO2?.REQUIRED)
        ?.required(ValidationMessages?.SPO2?.REQUIRED),
      systolic: Yup?.number()
        ?.typeError(ValidationMessages?.SYSTOLIC?.REQUIRED)
        ?.required(ValidationMessages?.SYSTOLIC?.REQUIRED),
      diastolic: Yup?.number()
        .typeError(ValidationMessages?.DIASTOLIC?.REQUIRED)
        .required(ValidationMessages?.DIASTOLIC?.REQUIRED),
      posture: Yup?.string().required(ValidationMessages?.POSTURE?.REQUIRED),
    }),
    history: Yup?.object()?.shape({
      height: Yup?.number()
        ?.typeError(ValidationMessages?.HEIGHT?.MIN)
        ?.min(1, ValidationMessages?.HEIGHT?.MIN)
        ?.required(textHeightRequire),
      weight: Yup?.number()
        ?.typeError(ValidationMessages?.WEIGHT?.MIN)
        ?.min(1, ValidationMessages?.WEIGHT?.MIN)
        ?.required(textWeightRequire),
    }),
  })

  return (
    <Formik<VitalsFormValues>
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {(formikProps: FormikProps<VitalsFormValues>) => {
        const { errors, touched, isSubmitting } = formikProps
        return (
          <Form>
            <Box paddingBottom={'80px'}>
              <SectionTitle variant='h6' gutterBottom>
                Patient Basic Health Info
              </SectionTitle>

              {/* Vitals Section */}
              <StyledCard>
                <StyledCardHeader
                  title='Vital Signs'
                  onClick={() => toggleSection('vitals')}
                  action={
                    <ExpandButton
                      onClick={(e) => {
                        e?.stopPropagation()
                        toggleSection('vitals')
                      }}
                      size='small'
                    >
                      {expanded?.vitals ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </ExpandButton>
                  }
                />
                <Collapse in={expanded?.vitals}>
                  <StyledCardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Field name='vitals.pulse'>
                          {({ field, meta }: any) => (
                            <StyledTextField
                              {...field}
                              fullWidth
                              label='Pulse (bpm) '
                              variant='outlined'
                              size='small'
                              type='number'
                              required
                              error={meta?.touched && !!meta?.error}
                              helperText={meta?.touched && meta?.error}
                            />
                          )}
                        </Field>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Field name='vitals.temperature'>
                          {({ field, meta }: any) => (
                            <StyledTextField
                              {...field}
                              fullWidth
                              label='Temperature (°F) '
                              variant='outlined'
                              size='small'
                              type='number'
                              required
                              error={meta?.touched && !!meta?.error}
                              helperText={meta?.touched && meta?.error}
                            />
                          )}
                        </Field>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Field name='vitals.respiratoryRate'>
                          {({ field, meta }: any) => (
                            <StyledTextField
                              {...field}
                              fullWidth
                              label='Respiratory Rate '
                              variant='outlined'
                              size='small'
                              type='number'
                              required
                              error={meta?.touched && !!meta?.error}
                              helperText={meta?.touched && meta?.error}
                            />
                          )}
                        </Field>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Field name='vitals.spo2'>
                          {({ field, meta }: any) => (
                            <StyledTextField
                              {...field}
                              fullWidth
                              label='SpO2 (%) '
                              variant='outlined'
                              size='small'
                              type='number'
                              required
                              error={meta?.touched && !!meta?.error}
                              helperText={meta?.touched && meta?.error}
                            />
                          )}
                        </Field>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Field name='vitals.systolic'>
                          {({ field, meta }: any) => (
                            <StyledTextField
                              {...field}
                              fullWidth
                              label='Systolic BP '
                              variant='outlined'
                              size='small'
                              type='number'
                              required
                              error={meta?.touched && !!meta?.error}
                              helperText={meta?.touched && meta?.error}
                            />
                          )}
                        </Field>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Field name='vitals.diastolic'>
                          {({ field, meta }: any) => (
                            <StyledTextField
                              {...field}
                              fullWidth
                              label='Diastolic BP'
                              variant='outlined'
                              size='small'
                              type='number'
                              required
                              error={meta?.touched && !!meta?.error}
                              helperText={meta?.touched && meta?.error}
                            />
                          )}
                        </Field>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl
                          fullWidth
                          size='small'
                          error={
                            touched?.vitals?.posture &&
                            !!errors?.vitals?.posture
                          }
                        >
                          <InputLabel>Posture</InputLabel>
                          <Field name='vitals.posture'>
                            {({ field }: FieldProps) => (
                              <Select
                                {...field}
                                label='Posture'
                                disabled={isView}
                                error={
                                  touched?.vitals?.posture &&
                                  !!errors?.vitals?.posture
                                }
                              >
                                {postureOptions?.map((option: any) => (
                                  <MenuItem
                                    key={option?.value}
                                    value={option?.value}
                                  >
                                    {option?.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          </Field>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={6} lg={6} xl={4}>
                        {/* <Field name='history.height'>
                          {({ field, meta }: FieldProps) => (
                            <StyledTextField
                              {...field}
                              fullWidth
                              label='Height (cm)'
                              variant='outlined'
                              size='small'
                              type='number'
                              required
                              error={meta?.touched && !!meta?.error}
                              helperText={meta?.touched && meta?.error}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position='end'>
                                    cm
                                  </InputAdornment>
                                ),
                              }}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                field?.onChange(e)
                                updateBMI(
                                  formikProps?.values?.history?.weight,
                                  e?.target?.value,
                                  formikProps
                                )
                              }}
                            />
                          )}
                        </Field> */}
                        <Height
                          label='Height *'
                          value={Number(formikProps?.values?.history?.height)}
                          onChange={(value: any) => {
                            formikProps?.setFieldValue('history.height', value)
                            updateBMI(
                              formikProps?.values?.history?.weight,
                              value,
                              formikProps
                            )
                          }}
                          // required={true}
                          error={
                            !!formikProps?.errors?.history?.height &&
                            formikProps?.touched?.history?.height
                          }
                          helperText={
                            formikProps?.errors?.history?.height &&
                            formikProps?.touched?.history?.height
                              ? formikProps?.errors?.history?.height
                              : ''
                          }
                          defaultDisplayMode='cm'
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Field name='history.weight'>
                          {({ field, meta }: FieldProps) => (
                            <StyledTextField
                              {...field}
                              fullWidth
                              label='Weight (kg)'
                              variant='outlined'
                              size='small'
                              type='number'
                              required
                              error={meta?.touched && !!meta?.error}
                              helperText={meta?.touched && meta?.error}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position='end'>
                                    kg
                                  </InputAdornment>
                                ),
                              }}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                field?.onChange(e)
                                updateBMI(
                                  e?.target?.value,
                                  formikProps?.values?.history?.height,
                                  formikProps
                                )
                              }}
                            />
                          )}
                        </Field>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Field name='vitals.bmi'>
                          {({ field }: FieldProps) => (
                            <StyledTextField
                              {...field}
                              fullWidth
                              label='BMI'
                              variant='outlined'
                              size='small'
                              type='text'
                              disabled
                              value={field.value || ''}
                              InputProps={{
                                readOnly: true,
                              }}
                            />
                          )}
                        </Field>
                      </Grid>
                      {/* Document Upload */}
                      {/* <Grid item xs={12}>
                        <Field name='vitals.document'>
                          {({ field, form }: FieldProps) => (
                            <div>
                              {!form?.values?.vitals?.documentUrl &&
                                !isUploading && (
                                  <>
                                    <input
                                      accept='image/*,.pdf,.doc,.docx'
                                      className={styles.hiddenFileInput}
                                      id='document-upload'
                                      type='file'
                                      disabled={isUploading}
                                      onChange={async (event) => {
                                        const file =
                                          event.currentTarget.files?.[0]
                                        if (file) {
                                          form?.setFieldValue(
                                            'vitals.document',
                                            file
                                          )
                                          await handleFileUpload(file, form)
                                        }
                                      }}
                                    />
                                    <label htmlFor='document-upload'>
                                      <Button
                                        variant='outlined'
                                        component='span'
                                        disabled={isUploading}
                                        startIcon={<CloudUploadIcon />}
                                        className={styles.uploadButton}
                                      >
                                        Upload Document/Image
                                      </Button>
                                    </label>
                                  </>
                                )}

                              {(field?.value ||
                                form?.values?.vitals?.documentUrl) && (
                                <div className={styles.documentWrapper}>
                                  <Box className={styles.documentContainer}>
                                    <div className={styles.documentContent}>
                                      <DescriptionIcon
                                        color='primary'
                                        className={styles.documentIcon}
                                      />
                                      <Typography
                                        variant='body2'
                                        className={styles.documentText}
                                        title={
                                          field.value?.name ||
                                          'Uploaded Document'
                                        }
                                      >
                                        {isUploading
                                          ? 'Uploading...'
                                          : field.value?.name ||
                                            'Document uploaded'}
                                      </Typography>
                                      <div className={styles.deleteButton}>
                                        <Tooltip title='Delete Document'>
                                          <IconButton
                                            size='small'
                                            onClick={(e) =>
                                              handleDeleteDocument(e, form)
                                            }
                                            color='error'
                                            aria-label='remove document'
                                          >
                                            <DeleteIcon fontSize='small' />
                                          </IconButton>
                                        </Tooltip>
                                      </div>
                                    </div>
                                  </Box>
                                  {uploadError && (
                                    <Typography
                                      color='error'
                                      variant='caption'
                                      className={styles.errorMessage}
                                    >
                                      {uploadError}
                                    </Typography>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </Field>
                      </Grid> */}
                      {/* Glucose Levels */}
                      <Grid item xs={12} sm={6} md={3}>
                        <Field name='vitals.glucoseType'>
                          {({ field, form }: FieldProps) => (
                            <FormControl fullWidth size='small'>
                              <InputLabel>Glucose Type</InputLabel>
                              <Select
                                {...field}
                                label='Glucose Type'
                                onChange={(e) => {
                                  form?.setFieldValue(
                                    'vitals.glucoseType',
                                    e?.target?.value
                                  )
                                  // Clear other glucose values when type changes
                                  if (
                                    e?.target?.value !== TYPES_OF_GLUCOSE.RANDOM
                                  )
                                    form?.setFieldValue(
                                      `vitals.${TYPES_OF_GLUCOSE.RANDOM}`,
                                      ''
                                    )
                                  if (
                                    e?.target?.value !==
                                    TYPES_OF_GLUCOSE.FASTING
                                  )
                                    form?.setFieldValue(
                                      `vitals.${TYPES_OF_GLUCOSE.FASTING}`,
                                      ''
                                    )
                                  if (
                                    e?.target?.value !==
                                    TYPES_OF_GLUCOSE.POSTPRANDIAL
                                  )
                                    form?.setFieldValue(
                                      `vitals.${TYPES_OF_GLUCOSE.POSTPRANDIAL}`,
                                      ''
                                    )
                                }}
                              >
                                {GLUCOSE_TYPES?.map((type) => (
                                  <MenuItem
                                    key={type?.value}
                                    value={type?.value}
                                  >
                                    {type?.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </Field>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Field
                          name={`vitals.${
                            formikProps?.values?.vitals?.glucoseType ||
                            TYPES_OF_GLUCOSE.RANDOM
                          }`}
                        >
                          {({ field, meta }: FieldProps) => (
                            <StyledTextField
                              {...field}
                              fullWidth
                              label={
                                GLUCOSE_TYPES?.find(
                                  (type) =>
                                    type?.value ===
                                    (formikProps?.values?.vitals?.glucoseType ||
                                      TYPES_OF_GLUCOSE?.RANDOM)
                                )?.label + TYPES_OF_GLUCOSE?.BLOOD_GLUCOSE
                              }
                              variant='outlined'
                              size='small'
                              type='number'
                              error={meta?.touched && !!meta?.error}
                              helperText={meta?.touched && meta?.error}
                              InputProps={{
                                inputProps: { min: 50, max: 500, step: '0.1' },
                              }}
                            />
                          )}
                        </Field>
                      </Grid>
                    </Grid>
                  </StyledCardContent>
                </Collapse>
              </StyledCard>

              {/* Chief Complaint Section */}
              <StyledCard>
                <StyledCardHeader
                  title='Chief Complaint'
                  onClick={() => toggleSection('chiefComplaints')}
                  action={
                    <ExpandButton
                      onClick={(e) => {
                        e?.stopPropagation()
                        toggleSection('chiefComplaints')
                      }}
                      size='small'
                      aria-label={
                        expanded?.chiefComplaints ? 'Collapse' : 'Expand'
                      }
                    >
                      {expanded?.chiefComplaints ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </ExpandButton>
                  }
                />
                <Collapse in={expanded?.chiefComplaints}>
                  <StyledCardContent>
                    <div className={styles.ChiefComplaintsContainer}>
                      <Typography
                        variant='h6'
                        className={styles.ChiefComplaintsTitle}
                      >
                        Chief Complaints
                      </Typography>
                      <FormGroup>
                        <div className={styles.ChiefComplaintsGridContainer}>
                          {complaints?.map((complaint) => (
                            <div
                              key={complaint?.id}
                              className={
                                styles.ChiefComplaintsComplaintItemVitals
                              }
                            >
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={complaint?.selected || false}
                                    onChange={handleComplaintChange(
                                      complaint?.id
                                    )}
                                    name={complaint?.name}
                                    size='small'
                                  />
                                }
                                label={complaint?.name}
                                className={styles.ChiefComplaintsFormLabel}
                              />
                              {complaint?.selected && (
                                <div
                                  className={
                                    styles.ChiefComplaintsDurationContainer
                                  }
                                >
                                  {complaint?.duration === 0 ? (
                                    <TextField
                                      size='small'
                                      type='number'
                                      value={
                                        complaint?.durationUnit === textHours
                                          ? complaint?.duration
                                          : ''
                                      }
                                      onChange={(e) =>
                                        handleCustomDurationChange(
                                          complaint?.id,
                                          e?.target?.value
                                        )
                                      }
                                      placeholder='Enter hours'
                                      className={`${styles.ChiefComplaintsDurationInput} ${styles.textFieldWhiteBg}`}
                                      variant='outlined'
                                      fullWidth
                                      InputProps={{
                                        endAdornment: (
                                          <span
                                            className={
                                              styles.ChiefComplaintsDurationUnit
                                            }
                                          >
                                            hours
                                          </span>
                                        ),
                                      }}
                                    />
                                  ) : (
                                    <TextField
                                      select
                                      size='small'
                                      value={complaint?.duration || 1}
                                      onChange={(e) =>
                                        handleDurationChange(
                                          complaint?.id,
                                          Number(e?.target?.value)
                                        )
                                      }
                                      className={`${styles.ChiefComplaintsDurationSelect} ${styles.textFieldWhiteBg}`}
                                      variant='outlined'
                                      fullWidth
                                    >
                                      {DURATION_OPTIONS?.map((option) => (
                                        <MenuItem
                                          key={option?.value}
                                          value={option?.value}
                                        >
                                          {option?.label}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </FormGroup>

                      <div className={styles.ChiefComplaintsCommentsSection}>
                        <Typography
                          variant='subtitle2'
                          className={styles.ChiefComplaintsCommentTitle}
                        >
                          Additional Comments
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          variant='outlined'
                          value={comment}
                          onChange={handleCommentChange}
                          placeholder='Enter any additional comments here...'
                          className={`${styles.ChiefComplaintsCommentField} ${styles.textFieldWhiteBg}`}
                        />
                      </div>
                    </div>
                  </StyledCardContent>
                </Collapse>
              </StyledCard>

              {/* Past Medical History Section */}
              <StyledCard>
                <StyledCardHeader
                  title='Past Medical History'
                  onClick={() => toggleSection('pastMedicalHistory')}
                  action={
                    <ExpandButton
                      onClick={(e) => {
                        e?.stopPropagation()
                        toggleSection('pastMedicalHistory')
                      }}
                      size='small'
                      aria-label={
                        expanded?.pastMedicalHistory ? 'Collapse' : 'Expand'
                      }
                    >
                      {expanded?.pastMedicalHistory ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </ExpandButton>
                  }
                />
                <Collapse in={expanded?.pastMedicalHistory}>
                  <StyledCardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant='subtitle2' gutterBottom>
                          Select relevant medical conditions:
                        </Typography>
                        <Grid
                          container
                          spacing={1}
                          className='medical-conditions-grid'
                        >
                          {medicalHistory.map((item) => (
                            <Grid item xs={6} sm={4} md={3} key={item.id}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size='small'
                                    checked={item.selected || false}
                                    onChange={() =>
                                      handleMedicalHistoryChange(item.id)
                                    }
                                    name={item.name}
                                  />
                                }
                                label={
                                  <Typography variant='body2'>
                                    {item.name}
                                  </Typography>
                                }
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Grid>

                      <Grid container spacing={2}>
                        {/* First Row */}
                        <Grid container item spacing={2} xs={12}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant='subtitle2' gutterBottom>
                              Previous Surgeries
                            </Typography>
                            <TextField
                              fullWidth
                              multiline
                              rows={2}
                              variant='outlined'
                              value={previousSurgeries}
                              onChange={(e) =>
                                setPreviousSurgeries(e.target.value)
                              }
                              placeholder='List any previous surgeries...'
                              className={styles.textFieldWhiteBg}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Typography variant='subtitle2' gutterBottom>
                              Drug Allergies
                            </Typography>
                            <TextField
                              fullWidth
                              multiline
                              rows={2}
                              variant='outlined'
                              value={drugAllergies}
                              onChange={(e) => setDrugAllergies(e.target.value)}
                              placeholder='List any drug allergies...'
                              className={styles.textFieldWhiteBg}
                            />
                          </Grid>
                        </Grid>

                        {/* Second Row */}
                        <Grid container item spacing={2} xs={12}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant='subtitle2' gutterBottom>
                              Other Conditions
                            </Typography>
                            <TextField
                              fullWidth
                              multiline
                              rows={2}
                              variant='outlined'
                              value={otherConditions}
                              onChange={(e) =>
                                setOtherConditions(e.target.value)
                              }
                              placeholder='Any other medical conditions...'
                              className={styles.textFieldWhiteBg}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Typography variant='subtitle2' gutterBottom>
                              Additional Notes
                            </Typography>
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              variant='outlined'
                              value={additionalNotes}
                              onChange={(e) =>
                                setAdditionalNotes(e.target.value)
                              }
                              placeholder='Enter any additional notes...'
                              className={styles.textFieldWhiteBg}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </StyledCardContent>
                </Collapse>
              </StyledCard>

              {/* History and Examination Section */}
              <StyledCard>
                <StyledCardHeader
                  title='History and Examination'
                  onClick={() => toggleSection('history')}
                  action={
                    <ExpandButton
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleSection('history')
                      }}
                      size='small'
                    >
                      {expanded?.history ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </ExpandButton>
                  }
                />
                <Collapse in={expanded?.history}>
                  <StyledCardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant='subtitle2' gutterBottom>
                          General Information
                        </Typography>
                        <StyledDivider />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Field name='history.wbps'>
                          {({ field, meta }: FieldProps) => (
                            <FormControl
                              fullWidth
                              size='small'
                              error={meta?.touched && !!meta?.error}
                            >
                              <InputLabel>Wong Baker Pain Score</InputLabel>
                              <Select
                                {...field}
                                label='Wong Baker Pain Score'
                                renderValue={(value: any) =>
                                  PAIN_SCALE_MAPPING[
                                    value as keyof typeof PAIN_SCALE_MAPPING
                                  ] || ''
                                }
                              >
                                {Object?.entries(PAIN_SCALE_MAPPING)?.map(
                                  ([key, value]) => (
                                    <MenuItem key={key} value={key}>
                                      {value}
                                    </MenuItem>
                                  )
                                )}
                              </Select>
                              {meta?.touched && meta?.error && (
                                <Typography color='error' variant='caption'>
                                  {meta?.error}
                                </Typography>
                              )}
                            </FormControl>
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Field name='history.chiefComplaints'>
                          {({ field, meta }: FieldProps) => (
                            <StyledTextField
                              {...field}
                              fullWidth
                              label='Chief Complaints'
                              variant='outlined'
                              size='small'
                              multiline
                              rows={2}
                              error={meta?.touched && !!meta?.error}
                              helperText={meta?.touched && meta?.error}
                            />
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Field name='history.historyOfPresentIllness'>
                          {({ field, meta }: FieldProps) => (
                            <StyledTextField
                              {...field}
                              fullWidth
                              label='History of Present Illness'
                              variant='outlined'
                              size='small'
                              multiline
                              rows={2}
                              error={meta?.touched && !!meta?.error}
                              helperText={meta?.touched && meta?.error}
                            />
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Field name='history.pastHistory'>
                          {({ field, meta }: FieldProps) => (
                            <StyledTextField
                              {...field}
                              fullWidth
                              label='Past History'
                              variant='outlined'
                              size='small'
                              multiline
                              rows={2}
                              error={meta?.touched && !!meta?.error}
                              helperText={meta?.touched && meta?.error}
                            />
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Field name='history.obstetricHistory'>
                          {({ field, meta }: FieldProps) => (
                            <StyledTextField
                              {...field}
                              fullWidth
                              label='Obstetric History'
                              variant='outlined'
                              size='small'
                              multiline
                              rows={2}
                              error={meta?.touched && !!meta?.error}
                              helperText={meta?.touched && meta?.error}
                            />
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Field name='history.historyDrugAllergies'>
                          {({ field, meta }: FieldProps) => (
                            <FormControl
                              fullWidth
                              size='small'
                              error={meta?.touched && !!meta?.error}
                            >
                              <InputLabel>History Of Drug Allergies</InputLabel>
                              <Select
                                {...field}
                                label='History Of Drug Allergies'
                              >
                                {yesNoOptions?.map((option) => (
                                  <MenuItem
                                    key={option?.value}
                                    value={option?.value}
                                  >
                                    {option?.label}
                                  </MenuItem>
                                ))}
                              </Select>
                              {meta?.touched && meta?.error && (
                                <Typography color='error' variant='caption'>
                                  {meta?.error}
                                </Typography>
                              )}
                            </FormControl>
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Field name='history.previousSurgeries'>
                          {({ field, meta }: FieldProps) => (
                            <FormControl
                              fullWidth
                              size='small'
                              error={meta?.touched && !!meta?.error}
                            >
                              <InputLabel>
                                History Of Previous Surgeries
                              </InputLabel>
                              <Select
                                {...field}
                                label='History Of Previous Surgeries'
                              >
                                {yesNoOptions?.map((option) => (
                                  <MenuItem
                                    key={option?.value}
                                    value={option?.value}
                                  >
                                    {option?.label}
                                  </MenuItem>
                                ))}
                              </Select>
                              {meta?.touched && meta?.error && (
                                <Typography color='error' variant='caption'>
                                  {meta?.error}
                                </Typography>
                              )}
                            </FormControl>
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant='subtitle2' gutterBottom>
                          Anthropometric Measurements
                        </Typography>
                        <StyledDivider />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Field name='history.diet'>
                          {({ field, meta }: FieldProps) => (
                            <FormControl
                              fullWidth
                              size='small'
                              error={meta?.touched && !!meta?.error}
                            >
                              <InputLabel>Diet</InputLabel>
                              <Select {...field} label='Diet'>
                                {dietOptions?.map((option) => (
                                  <MenuItem
                                    key={option?.value}
                                    value={option?.value}
                                  >
                                    {option?.label}
                                  </MenuItem>
                                ))}
                              </Select>
                              {meta?.touched && meta?.error && (
                                <Typography color='error' variant='caption'>
                                  {meta?.error}
                                </Typography>
                              )}
                            </FormControl>
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Field name='history.sleep'>
                          {({ field, meta }: FieldProps) => (
                            <FormControl
                              fullWidth
                              size='small'
                              error={meta?.touched && !!meta?.error}
                            >
                              <InputLabel>Sleep</InputLabel>
                              <Select {...field} label='Sleep'>
                                {sleepOptions?.map((option) => (
                                  <MenuItem
                                    key={option?.value}
                                    value={option?.value}
                                  >
                                    {option?.label}
                                  </MenuItem>
                                ))}
                              </Select>
                              {meta?.touched && meta?.error && (
                                <Typography color='error' variant='caption'>
                                  {meta?.error}
                                </Typography>
                              )}
                            </FormControl>
                          )}
                        </Field>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Field name='history.bowelBladder'>
                          {({ field, meta }: FieldProps) => (
                            <FormControl
                              fullWidth
                              size='small'
                              error={meta?.touched && !!meta?.error}
                            >
                              <InputLabel>Bowel & Bladder</InputLabel>
                              <Select {...field} label='Bowel & Bladder'>
                                {bowelBladderOptions?.map((option) => (
                                  <MenuItem
                                    key={option?.value}
                                    value={option?.value}
                                  >
                                    {option?.label}
                                  </MenuItem>
                                ))}
                              </Select>
                              {meta?.touched && meta?.error && (
                                <Typography color='error' variant='caption'>
                                  {meta?.error}
                                </Typography>
                              )}
                            </FormControl>
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12}>
                        <SectionSubtitle variant='subtitle2' gutterBottom>
                          Physical Examination
                        </SectionSubtitle>
                        <StyledDivider />
                      </Grid>

                      <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Field name='history.pallor'>
                          {({ field, meta }: FieldProps) => (
                            <FormControl
                              fullWidth
                              size='small'
                              error={meta?.touched && !!meta?.error}
                            >
                              <InputLabel>Pallor</InputLabel>
                              <Select {...field} label='Pallor'>
                                {yesNoOptions?.map((option) => (
                                  <MenuItem
                                    key={option?.value}
                                    value={option?.value}
                                  >
                                    {option?.label}
                                  </MenuItem>
                                ))}
                              </Select>
                              {meta?.touched && meta?.error && (
                                <Typography color='error' variant='caption'>
                                  {meta?.error}
                                </Typography>
                              )}
                            </FormControl>
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Field name='history.icterus'>
                          {({ field, meta }: FieldProps) => (
                            <FormControl
                              fullWidth
                              size='small'
                              error={meta?.touched && !!meta?.error}
                            >
                              <InputLabel>Icterus</InputLabel>
                              <Select {...field} label='Icterus'>
                                {yesNoOptions?.map((option) => (
                                  <MenuItem
                                    key={option?.value}
                                    value={option?.value}
                                  >
                                    {option?.label}
                                  </MenuItem>
                                ))}
                              </Select>
                              {meta?.touched && meta?.error && (
                                <Typography color='error' variant='caption'>
                                  {meta?.error}
                                </Typography>
                              )}
                            </FormControl>
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Field name='history.cyanosis'>
                          {({ field, meta }: FieldProps) => (
                            <FormControl
                              fullWidth
                              size='small'
                              error={meta?.touched && !!meta?.error}
                            >
                              <InputLabel>Cyanosis</InputLabel>
                              <Select {...field} label='Cyanosis'>
                                {yesNoOptions?.map((option) => (
                                  <MenuItem
                                    key={option?.value}
                                    value={option?.value}
                                  >
                                    {option?.label}
                                  </MenuItem>
                                ))}
                              </Select>
                              {meta?.touched && meta?.error && (
                                <Typography color='error' variant='caption'>
                                  {meta?.error}
                                </Typography>
                              )}
                            </FormControl>
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Field name='history.clubbing'>
                          {({ field, meta }: FieldProps) => (
                            <FormControl
                              fullWidth
                              size='small'
                              error={meta?.touched && !!meta?.error}
                            >
                              <InputLabel>Clubbing</InputLabel>
                              <Select {...field} label='Clubbing'>
                                {yesNoOptions?.map((option) => (
                                  <MenuItem
                                    key={option?.value}
                                    value={option?.value}
                                  >
                                    {option?.label}
                                  </MenuItem>
                                ))}
                              </Select>
                              {meta?.touched && meta?.error && (
                                <Typography color='error' variant='caption'>
                                  {meta?.error}
                                </Typography>
                              )}
                            </FormControl>
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Field name='history.lymphadenopathy'>
                          {({ field, meta }: FieldProps) => (
                            <FormControl
                              fullWidth
                              size='small'
                              error={meta?.touched && !!meta?.error}
                            >
                              <InputLabel>Lymphadenopathy</InputLabel>
                              <Select {...field} label='Lymphadenopathy'>
                                {yesNoOptions?.map((option) => (
                                  <MenuItem
                                    key={option?.value}
                                    value={option?.value}
                                  >
                                    {option?.label}
                                  </MenuItem>
                                ))}
                              </Select>
                              {meta?.touched && meta?.error && (
                                <Typography color='error' variant='caption'>
                                  {meta?.error}
                                </Typography>
                              )}
                            </FormControl>
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Field name='history.edema'>
                          {({ field, meta }: FieldProps) => (
                            <FormControl
                              fullWidth
                              size='small'
                              error={meta?.touched && !!meta?.error}
                            >
                              <InputLabel>Edema</InputLabel>
                              <Select {...field} label='Edema'>
                                {yesNoOptions?.map((option) => (
                                  <MenuItem
                                    key={option?.value}
                                    value={option?.value}
                                  >
                                    {option?.label}
                                  </MenuItem>
                                ))}
                              </Select>
                              {meta?.touched && meta?.error && (
                                <Typography color='error' variant='caption'>
                                  {meta?.error}
                                </Typography>
                              )}
                            </FormControl>
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Field name='history.smokingHistory'>
                          {({ field, meta }: FieldProps) => (
                            <FormControl
                              fullWidth
                              size='small'
                              error={meta?.touched && !!meta?.error}
                            >
                              <InputLabel>Smoking History</InputLabel>
                              <Select {...field} label='Smoking History'>
                                {yesNoOptions?.map((option) => (
                                  <MenuItem
                                    key={option?.value}
                                    value={option?.value}
                                  >
                                    {option?.label}
                                  </MenuItem>
                                ))}
                              </Select>
                              {meta?.touched && meta?.error && (
                                <Typography color='error' variant='caption'>
                                  {meta?.error}
                                </Typography>
                              )}
                            </FormControl>
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Field name='history.alcoholicHistory'>
                          {({ field, meta }: FieldProps) => (
                            <FormControl
                              fullWidth
                              size='small'
                              error={meta?.touched && !!meta?.error}
                            >
                              <InputLabel>Alcoholic History</InputLabel>
                              <Select {...field} label='Alcoholic History'>
                                {yesNoOptions?.map((option) => (
                                  <MenuItem
                                    key={option?.value}
                                    value={option?.value}
                                  >
                                    {option?.label}
                                  </MenuItem>
                                ))}
                              </Select>
                              {meta?.touched && meta?.error && (
                                <Typography color='error' variant='caption'>
                                  {meta?.error}
                                </Typography>
                              )}
                            </FormControl>
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12}>
                        <Field name='history.habitualHistory'>
                          {({ field, meta }: FieldProps) => (
                            <StyledTextField
                              {...field}
                              fullWidth
                              label='Habitual History'
                              variant='outlined'
                              size='small'
                              multiline
                              rows={2}
                              error={meta?.touched && !!meta?.error}
                              helperText={meta?.touched && meta?.error}
                            />
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12}>
                        <Field name='history.pastMedicalHistory'>
                          {({ field, meta }: FieldProps) => (
                            <StyledTextField
                              {...field}
                              fullWidth
                              label='Past Medical History'
                              variant='outlined'
                              size='small'
                              multiline
                              rows={2}
                              error={meta?.touched && !!meta?.error}
                              helperText={meta?.touched && meta?.error}
                            />
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12}>
                        <Field name='history.familyHistory'>
                          {({ field, meta }: FieldProps) => (
                            <StyledTextField
                              {...field}
                              fullWidth
                              label='Family History'
                              variant='outlined'
                              size='small'
                              multiline
                              rows={2}
                              error={meta?.touched && !!meta?.error}
                              helperText={meta?.touched && meta?.error}
                            />
                          )}
                        </Field>
                      </Grid>
                    </Grid>
                  </StyledCardContent>
                </Collapse>
              </StyledCard>

              {/* Systemic Examination Section */}
              <StyledCard>
                <StyledCardHeader
                  title='Systemic Examination'
                  onClick={() => toggleSection('systemicExamination')}
                  action={
                    <ExpandButton
                      onClick={(e) => {
                        e?.stopPropagation()
                        toggleSection('systemicExamination')
                      }}
                      size='small'
                    >
                      {expanded?.systemicExamination ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </ExpandButton>
                  }
                />
                <Collapse in={expanded?.systemicExamination}>
                  <StyledCardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Field name='systemic.localExamination'>
                          {({ field, meta }: any) => (
                            <StyledTextField
                              {...field}
                              fullWidth
                              label='Local Examination'
                              variant='outlined'
                              size='small'
                              multiline
                              rows={2}
                              error={meta?.touched && !!meta?.error}
                              helperText={meta?.touched && meta?.error}
                            />
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Field name='systemic.cardiovascularSystem'>
                          {({ field, meta }: any) => (
                            <StyledTextField
                              {...field}
                              fullWidth
                              label='Cardiovascular System'
                              variant='outlined'
                              size='small'
                              multiline
                              rows={2}
                              error={meta?.touched && !!meta?.error}
                              helperText={meta?.touched && meta?.error}
                            />
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Field name='systemic.respiratorySystem'>
                          {({ field, meta }: any) => (
                            <StyledTextField
                              {...field}
                              fullWidth
                              label='Respiratory System'
                              variant='outlined'
                              size='small'
                              multiline
                              rows={2}
                              error={meta?.touched && !!meta?.error}
                              helperText={meta?.touched && meta?.error}
                            />
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Field name='systemic.nervousSystem'>
                          {({ field, meta }: any) => (
                            <StyledTextField
                              {...field}
                              fullWidth
                              label='Nervous System'
                              variant='outlined'
                              size='small'
                              multiline
                              rows={2}
                              error={meta?.touched && !!meta?.error}
                              helperText={meta?.touched && meta?.error}
                            />
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Field name='systemic.perAbdomen'>
                          {({ field, meta }: any) => (
                            <StyledTextField
                              {...field}
                              fullWidth
                              label='Per Abdomen'
                              variant='outlined'
                              size='small'
                              multiline
                              rows={2}
                              error={meta?.touched && !!meta?.error}
                              helperText={meta?.touched && meta?.error}
                            />
                          )}
                        </Field>
                      </Grid>
                    </Grid>
                  </StyledCardContent>
                </Collapse>
              </StyledCard>

              {/* Submit Button */}
              <ButtonContainer>
                <SubmitButton
                  type='submit'
                  variant='contained'
                  color='primary'
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save All'}
                </SubmitButton>
              </ButtonContainer>
            </Box>
          </Form>
        )
      }}
    </Formik>
  )
}

export default Vitals
